import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import NextImage from 'src/shared-components/NextImage/NextImage';
import NavigationLink from '../NavigationLink/NavigationLink';
import { GeneratedLinkUrlObject } from '../../utils/links';

const dots = 'search/dots.svg';

interface Props {
  selectedPages: number[];
  lastPageIndex: number;
  getPaginationLink: (index: number) => GeneratedLinkUrlObject;
}
interface PageNumberProps {
  selectedPages: number[];
  pageIndex: number;
  getPaginationLink: (pageIndex: number) => GeneratedLinkUrlObject;
}

const PageNumber: FC<PageNumberProps> = ({
  pageIndex,
  getPaginationLink,
  selectedPages
}) => {
  const ButtonsStyles = {
    active: 'text-white bg-darkBlue border-darkBlue',
    inActiveList: 'text-astronautBlue bg-white border-astronautBlue',
    inactive: 'text-silver bg-white border-silver'
  };
  const getButtonStyles = () => {
    if (pageIndex === selectedPages[selectedPages?.length - 1]) {
      return ButtonsStyles.active;
    }
    return selectedPages?.includes(pageIndex)
      ? ButtonsStyles.inActiveList
      : ButtonsStyles.inactive;
  };
  const [link, setLink] = useState(getPaginationLink(pageIndex));

  useEffect(() => {
    setLink(getPaginationLink(pageIndex));
  }, [pageIndex, getPaginationLink]);

  return (
    <NavigationLink
      key={`pageLink_${pageIndex}`}
      href={link}
      passClassName={cn(
        'w-6 h-6 rounded-md border fs-caption-reg overflow-hidden flex items-center justify-center',
        getButtonStyles()
      )}
    >
      <p>{pageIndex}</p>
    </NavigationLink>
  );
};

const Pagination: FC<Props> = ({
  getPaginationLink,
  lastPageIndex,
  selectedPages
}) => {
  // Magical number that represent the amount of number to be shown between dots
  const visibleCount = 4;

  const getNumbersToShow = (): number[] => {
    const lastItem = selectedPages[0];
    if (lastPageIndex <= visibleCount) {
      const arr = Array.from(Array(lastPageIndex + 1).keys());
      return arr.splice(1, lastPageIndex + 1);
    }
    if (selectedPages?.length >= visibleCount) {
      return selectedPages;
    }
    if (lastItem <= visibleCount) {
      const arr = Array.from(Array(visibleCount + 2).keys());
      return arr.splice(1, visibleCount + 2);
    }
    if (lastItem + visibleCount > lastPageIndex) {
      const arr = Array.from(Array(lastPageIndex + 1).keys());
      return arr.splice(lastPageIndex - visibleCount, visibleCount + 1);
    }

    if (lastItem) {
      const arr = Array.from(Array(lastItem + 3).keys());
      return arr.splice(lastItem - visibleCount + 2, visibleCount + 2);
    }
    return [];
  };

  const numbersToShow = getNumbersToShow();
  return (
    <div className="flex mt-11 space-x-2">
      {!numbersToShow?.includes(1) && (
        <>
          <PageNumber
            key="firstPageController_1"
            pageIndex={1}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
          <div className="flex items-end">
            <NextImage
              alt="dots icon"
              layout="fixed"
              height={3}
              width={21}
              src={dots}
            />
          </div>
        </>
      )}
      {getNumbersToShow().map((value) => {
        return (
          <PageNumber
            key={`middlePageController_${value}`}
            pageIndex={value}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
        );
      })}
      {!numbersToShow?.includes(lastPageIndex) && (
        <>
          <div className="flex items-end">
            <NextImage
              alt="dots icon"
              layout="fixed"
              height={3}
              width={21}
              src={dots}
            />
          </div>
          <PageNumber
            key={`lastPageController_${lastPageIndex}`}
            pageIndex={lastPageIndex}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
        </>
      )}
    </div>
  );
};

export default Pagination;
